import React from "react";
// import ReactDOM from "react-dom"; // escape hatch for tooltips
import { connect } from "react-redux";
import { range } from "d3-array";
import { Slider, Toggle } from "carbon-components-react";
import { compose, withHandlers } from "recompose";


// Popups aren't working right now, revisit another week, just use title text for now.
// import { Popup } from "semantic-ui-react";
// import ReactTooltip from "react-tooltip";


import { setScenarioNumber, setScenarioBoolean } from "../../../store/actions/scenarios";

import styles from "./styles.module.scss";

import {
  getModalSplitNonMotorizedTransitLever,
  getModalSplitPublicTransitLever,
  getModalSplitSharedMobilityLever,
  getPrivateElectrificationVehicleGoal,
  getPublicCommercialElectrificationVehicleGoal,
  // Boolean
  getHasRenewableEnergyTarget
} from "../../../store/selectors";

const LEVER_TYPE = {
  nonMotorizedTransit: "nonMotorizedTransit",
  publicTransit: "publicTransit",
  sharedMobility: "sharedMobility",
  privateElectrificationVehicleGoal: "privateVehicle", // TODO: cleanup leakage: has awareness of how store is shaped
  publicCommercialElectrificationVehicleGoal: "publicAndCommercial"
};

const LEVER_SELECTOR = {
  [LEVER_TYPE.nonMotorizedTransit]: getModalSplitNonMotorizedTransitLever,
  [LEVER_TYPE.publicTransit]: getModalSplitPublicTransitLever,
  [LEVER_TYPE.sharedMobility]: getModalSplitSharedMobilityLever,
  [LEVER_TYPE.privateElectrificationVehicleGoal]: getPrivateElectrificationVehicleGoal,
  [LEVER_TYPE.publicCommercialElectrificationVehicleGoal]: getPublicCommercialElectrificationVehicleGoal
};

// This is used by both selectors on the page
const makeMapStateToProps = (initialState, initialProps) => {
  const { leverName } = initialProps;
  const selector = LEVER_SELECTOR[leverName];
  const mapStateToProps = (state, props) => {
    return {
      value: selector(state, props), // some number
    }
  }
  return mapStateToProps;
}
const mapDispatchToProps = {
  setScenarioNumber,
}

// Actual components go below!!


/**
* Explanatory text to accompany each number selection bubble. Not DRY.
* @param leverName: item from LEVER_TYPE enum
* @param number: int from 0 to 4
*/
const getTooltipText = ({leverName, number}) => {
  if (leverName === LEVER_TYPE.nonMotorizedTransit) {
    switch (number) {
      case 0:
        return "Business as Usual. What would happen if your city developed the way it currently is?";
      case 1:
        return "A mild amount of investment in non-motorised transit and slight reduction in passenger kilometers traveled.";
      case 2:
        return "A moderate amount of investment in non-motorised transit and mobility oriented development.";
      case 3:
        return "A large amount of investment in non-motorised transit and mobility oriented development.";
    }
  }

  if (leverName === LEVER_TYPE.publicTransit) {
    switch (number) {
      case 0:
        return "Business as Usual";
      case 1:
        return "A mild amount of investment in public transit solutions, with a slight increase from BAU in the modal split for public transit.";
      case 2:
        return "A moderate amount of investment in public transit solutions";
      case 3:
        return "A large amount of investment in public transit solutions.";
    }
  }

  if (leverName === LEVER_TYPE.sharedMobility) {
    switch (number) {
      case 0:
        return "Business as Usual.";
      case 1:
        return "A mild amount of investment in shared mobility, with a slight increase from BAU in the modal split for commercial vehicles. ";
      case 2:
        return "A moderate amount of investment in shared mobility";
      case 3:
        return "A large amount of investment in shared mobility.";
    }
  }
  return "";
}


const NumberBubble = (props) => (
  <span
      className={props.className}
      onClick={props.handleClick}
      data-value={props.number}
      title={getTooltipText(props)}>
    { props.number }
  </span>
);

// Defunct semantic-ui popup
{/* <Popup
  key={index}
  trigger={<NumberBubble  {...bubbleProps} />}
  on='hover'
// open={true}
>
  <Popup.Header>User Rating</Popup.Header>
  <Popup.Content>
    <p>Hello THIS IS ME!!!!!!!!!</p>
  </Popup.Content>
</Popup> */}

// https://github.com/wwayne/react-tooltip/issues/219
{/* <ReactTooltip afterHide={() => {
  var node = ReactDOM.findDOMNode(this.refs.tooltipComponent);
  node.style.left = null;
  node.style.top = null;
}} /> */}


const BaseNumberPicker = (props) => {
  const numbers = range(props.start, props.end);
  const { value, handleClick, leverName } = props;
  return <div className={styles.numberPicker}>
      {numbers.map((number, index) => {
        const className = value === number ? styles.active : "";
        const bubbleProps = { className, handleClick, number, leverName };
        return <NumberBubble {...bubbleProps} />;
      })}

    </div>;
}

// Communicate more clearly that this is the circle number selector
const ConnectedBubblePicker = compose(
  connect(
    makeMapStateToProps,
    mapDispatchToProps
  ),
  withHandlers({
    handleClick: props => event => {
      const value = +event.target.getAttribute("data-value");
      const { leverName } = props;
      props.setScenarioNumber(leverName, value, props.scenarioKey);
    }
  })
)(BaseNumberPicker);

const BaseSlider = props => {

  let labelProps = {
    minLabel: "%",
    maxLabel: "%"
  }

  if (props.hideLabels) {
    labelProps = {
      formatLabel: props.formatLabel
    }
  }

  return (
  <Slider
    value={props.value}
    min={0}
    max={100}
    light={true}
    onChange={props.handleChange}
    {...labelProps}
  />
)};

const ConnectedSlider = compose(
  connect(
    makeMapStateToProps,
    mapDispatchToProps
  ),
  withHandlers({
    handleChange: props => event => {
      const { value } = event;
      const { leverName } = props;
      props.setScenarioNumber(leverName, value, props.scenarioKey);
    },
    formatLabel: props => () => "", // blank
  })
)(BaseSlider);


// Toggle
const BaseToggle = (props) => {
  return (
    <Toggle
      className={`toggle-${props.scenarioKey}`}
      labelA="No"
      labelB="Yes"
      id={`toggle-${props.scenarioKey}`} // MUST BE UNIQUE
      onToggle={props.handleChange}
      toggled={props.toggled}
    />
  );
}


const makeMapStateToPropsToggle = () => {
  const mapStateToPropsToggle = (state, props) => {
    return {
      toggled: getHasRenewableEnergyTarget(state, props)
    }
  }
  return mapStateToPropsToggle;
}

const mapDispatchToPropsToggle = {
  setScenarioBoolean
};

const ConnectedToggle = compose(
  connect(
    makeMapStateToPropsToggle,
    mapDispatchToPropsToggle
  ),
  withHandlers({
    handleChange: props => isTrue => {
      // callback passes boolean directly
      const { leverName, scenarioKey } = props;
      props.setScenarioBoolean(leverName, isTrue, scenarioKey);
    }
  })
)(BaseToggle);


export { ConnectedBubblePicker, ConnectedSlider, ConnectedToggle, LEVER_TYPE } ;



// WEBPACK FOOTER //
// ./src/pages/modelLevers/panes/numberSelectors.js